import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { Loader2, MailCheck } from 'lucide-react';
import { BANNED_MESSAGE, isBannedError } from '../lib/banned';

const QUEUED_NOTE = 'Our e-mail service is busier than usual — your code is on its way and can take a few minutes to arrive. Check your spam folder too.';

export function OtpVerify({ email, onVerified, autoSend = false, delivery = '' }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [queued, setQueued] = useState(delivery === 'queued');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(autoSend ? 0 : 60);
  const sentRef = useRef(false);

  const resend = React.useCallback(async (silent = false) => {
    setError('');
    setInfo('');
    try {
      const { data } = await api.post('/api/auth/resend-otp', { email });
      setCooldown(60);
      setQueued(data?.otp_delivery === 'queued');
      if (!silent) setInfo('A new code has been sent to your email.');
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 429) {
        setCooldown(60);
        if (silent) setInfo('Use the code we already sent to your email.');
        else setError(typeof detail === 'string' ? detail : 'Please wait before requesting a new code.');
      } else if (!silent) {
        setError(typeof detail === 'string' ? detail : 'Could not resend the code.');
      }
    }
  }, [email]);

  useEffect(() => {
    if (autoSend && !sentRef.current) {
      sentRef.current = true;
      resend(true);
    }
  }, [autoSend, resend]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const verify = async (e) => {
    e?.preventDefault();
    if (code.trim().length !== 6) { setError('Enter the 6-digit code'); return; }
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post('/api/auth/verify-otp', { email, code: code.trim() });
      onVerified(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(isBannedError(err) ? BANNED_MESSAGE : (typeof detail === 'string' ? detail : 'Verification failed'));
      setBusy(false);
    }
  };

  return (
    <div className="text-center" data-testid="otp-verify">
      <div className="w-14 h-14 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mx-auto mb-4">
        <MailCheck size={26} />
      </div>
      <h1 className="text-xl font-bold text-neutral-900 mb-2" data-testid="otp-title">Check your email</h1>
      <p className="text-sm text-neutral-500 mb-6">
        We sent a 6-digit verification code to<br /><span className="font-semibold text-neutral-800">{email}</span>
      </p>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="otp-error">{error}</div>}
      {info && <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700" data-testid="otp-info">{info}</div>}
      {queued && <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="otp-queued-note">{QUEUED_NOTE}</div>}

      <form onSubmit={verify}>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={e => { setCode(e.target.value.replace(/\D/g, '')); setError(''); }}
          placeholder="••••••"
          autoFocus
          className="w-full px-4 py-3 border border-neutral-300 rounded-xl text-center text-2xl font-mono tracking-[0.5em] focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition mb-4"
          data-testid="otp-input"
        />
        <button type="submit" disabled={busy || code.length !== 6}
          className="w-full py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primary-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="otp-submit">
          {busy ? <><Loader2 size={14} className="animate-spin" />Verifying…</> : 'Verify email'}
        </button>
      </form>

      <p className="mt-5 text-sm text-neutral-500">
        Didn't get it?{' '}
        {cooldown > 0 ? (
          <span className="text-neutral-400" data-testid="otp-resend-cooldown">Resend in {cooldown}s</span>
        ) : (
          <button type="button" onClick={() => resend(false)} className="text-primary font-medium hover:underline" data-testid="otp-resend-btn">
            Resend code
          </button>
        )}
      </p>
      <p className="mt-2 text-xs text-neutral-400">Check your spam folder too.</p>
    </div>
  );
}
